import React, { useEffect, useState, useRef, lazy } from 'react';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
const ugcSlides = [{
  image: 'https://cdn.magicpatterns.com/uploads/uUPHNSxk2Afx2WfQjf3Z89/G1.png',
  name: 'Margaret T., 67',
  quote: 'My legs finally feel light again after years of heaviness.'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/hQGRbkVBGpKucHNqTz1pP6/UGC_1.png',
  name: 'Linda S., 54',
  quote: 'No greasy residue. Just real, lasting warmth.'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/jJ2L7Q5XreohRBnn7KgrEt/UGC_05.png',
  name: 'Rosa M., 72',
  quote: "I use it every morning. My ankles don't swell like before."
}, {
  image: 'https://cdn.magicpatterns.com/uploads/5pH2KQ1TBTEhYBKtSBD16t/UGC_06.png',
  name: 'Maria G., 58',
  quote: "I brought it on vacation — couldn't go a day without it."
}, {
  image: 'https://cdn.magicpatterns.com/uploads/5DWYRB7viZQzcoo7yhA7y9/UGC_03.png',
  name: 'Karen W., 49',
  quote: 'This replaced everything else in my routine.'
}];
export function UGCCarousel() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  };
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll, {
      passive: true
    });
    window.addEventListener('resize', checkScroll);
    return () => {
      el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, []);
  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.querySelector('.ugc-card')?.clientWidth ?? 280;
    const gap = 16;
    el.scrollBy({
      left: direction === 'left' ? -(cardWidth + gap) : cardWidth + gap,
      behavior: 'smooth'
    });
  };
  return <section className="py-10 sm:py-14 bg-[#FAF7F2]" data-id="element-1424">
      <div className="max-w-6xl mx-auto px-4" data-id="element-1425">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8" data-id="element-1426">
          <p className="text-xs font-extrabold tracking-[0.14em] uppercase text-ginger mb-2" data-id="element-1427">
            Real People, Real Relief
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-[#1A1A1A] leading-tight tracking-tight" data-id="element-1428">
            Trusted by 10,000+ Happy Customers
          </h2>
        </div>

        {/* Carousel Container */}
        <div className="relative" data-id="element-1429">
          {/* Left Arrow */}
          {canScrollLeft && <button onClick={() => scroll('left')} className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 sm:w-12 sm:h-12 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-full shadow-lg flex items-center justify-center hover:bg-white transition-colors -ml-2 sm:-ml-4" aria-label="Scroll left" data-id="element-1430">
              <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 text-charcoal" data-id="element-1431" />
            </button>}

          {/* Right Arrow */}
          {canScrollRight && <button onClick={() => scroll('right')} className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 sm:w-12 sm:h-12 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-full shadow-lg flex items-center justify-center hover:bg-white transition-colors -mr-2 sm:-mr-4" aria-label="Scroll right" data-id="element-1432">
              <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 text-charcoal" data-id="element-1433" />
            </button>}

          {/* Scrollable Track */}
          <div ref={scrollRef} className="flex gap-4 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-2 -mx-4 px-4" style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }} data-id="element-1434">
            {ugcSlides.map((slide, i) => <div key={i} className="ugc-card flex-shrink-0 w-[260px] sm:w-[280px] snap-start" data-id="element-1435">
                <div className="bg-white rounded-2xl overflow-hidden shadow-md border border-gray-100 h-full" data-id="element-1436">
                  {/* Image */}
                  <div className="w-full aspect-[3/4] overflow-hidden" data-id="element-1437">
                    <img src={slide.image} alt={`${slide.name} - GINEXT customer`} className="w-full h-full object-cover object-top" loading="lazy" decoding="async" width="280" height="373" data-id="element-1438" />
                  </div>

                  {/* Content */}
                  <div className="p-4" data-id="element-1439">
                    {/* Stars */}
                    <div className="flex gap-0.5 mb-2" data-id="element-1440">
                      {[...Array(5)].map((_, j) => <Star key={j} className="w-3.5 h-3.5 fill-ginger text-ginger" data-id="element-1441" />)}
                    </div>

                    {/* Quote */}
                    <p className="text-sm font-medium text-[#2D2D2D] leading-snug mb-2" data-id="element-1442">
                      "{slide.quote}"
                    </p>

                    {/* Name */}
                    <p className="text-xs font-semibold text-ginger" data-id="element-1443">
                      {slide.name}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>
        </div>

        {/* Dot Indicators */}
        <div className="flex justify-center gap-1.5 mt-4" data-id="element-1444">
          {ugcSlides.map((_, i) => <div key={i} className="w-2 h-2 rounded-full bg-ginger/30" data-id="element-1445" />)}
        </div>
      </div>
    </section>;
}